// Phase 4D.3B — explicit action confirmation card.
//
// Laws enforced here:
//   * Nothing has happened when this card renders. It is a proposal.
//   * Only pressing Confirm performs the write, exactly once.
//   * The confirmation token is held ephemerally in memory for that one
//     request and is never displayed, copied or persisted.
//   * Spoken or typed "yes" can never confirm — only this control can.

import { useCallback, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Loader2, ShieldCheck, TriangleAlert, X } from "lucide-react";
import { confirmIntelligenceWrite } from "@/lib/intelligence-tools.functions";
import type { PublicActionProposal } from "@/lib/intelligence/orchestration/contract";

type Phase = "proposed" | "working" | "done" | "failed" | "cancelled";

const WRITE_TOOLS = ["memory_set_enabled", "memory_save_note", "memory_forget"] as const;
type WriteTool = (typeof WRITE_TOOLS)[number];

function isWriteTool(name: string): name is WriteTool {
  return (WRITE_TOOLS as readonly string[]).includes(name);
}

export function ActionConfirmCard({ proposal }: { proposal: PublicActionProposal }) {
  const confirm = useServerFn(confirmIntelligenceWrite);
  const [phase, setPhase] = useState<Phase>("proposed");
  const [message, setMessage] = useState<string>(proposal.message);

  const run = useCallback(async () => {
    if (phase !== "proposed") return;
    if (!proposal.confirmationToken || !isWriteTool(proposal.tool)) {
      setPhase("failed");
      setMessage("This action can no longer be confirmed — nothing has been done.");
      return;
    }
    setPhase("working");
    try {
      const out = await confirm({
        data: {
          tool: proposal.tool,
          args: (proposal.args ?? {}) as never,
          confirmationToken: proposal.confirmationToken,
        },
      });
      if (out.ok) {
        setPhase("done");
        setMessage(out.result.detail);
      } else {
        setPhase("failed");
        setMessage(out.message);
      }
    } catch {
      setPhase("failed");
      setMessage("That could not be completed — nothing has been changed.");
    }
  }, [confirm, phase, proposal]);

  const blocked = proposal.status !== "confirmation_required" || !proposal.confirmationToken;

  return (
    <section
      aria-label="Action confirmation"
      className="mt-3 rounded-xl border border-gold/40 bg-gold/5 p-3"
    >
      <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gold">
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
        {phase === "done" ? "Done" : "Confirmation needed"}
      </h3>

      {proposal.summary && phase === "proposed" ? (
        <p className="mt-2 text-sm">{proposal.summary}</p>
      ) : null}
      <p role="status" aria-live="polite" className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
        {phase === "done" ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
        ) : phase === "failed" ? (
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        ) : null}
        <span>{message}</span>
      </p>

      {phase === "proposed" && !blocked ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void run()}
            className="inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-xs font-medium text-background"
          >
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Confirm
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase("cancelled");
              setMessage("Cancelled — nothing was changed.");
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-gold/50 hover:text-gold"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" /> Cancel
          </button>
        </div>
      ) : null}

      {phase === "working" ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Applying…
        </p>
      ) : null}
    </section>
  );
}

/** Collapsible, non-sensitive operational summary. Never reasoning. */
export function AgentTraceSummary({
  trace,
}: {
  trace: NonNullable<import("@/lib/intelligence/orchestration/contract").PublicAgentTrace>;
}) {
  if (!trace.ran) return null;
  return (
    <details className="mt-3 text-[11px] text-muted-foreground">
      <summary className="cursor-pointer select-none uppercase tracking-wider">
        Agent steps ({trace.executed}/{trace.planned})
      </summary>
      <ul className="mt-2 space-y-1">
        {trace.steps.map((s, i) => (
          <li key={`${s.tool}-${i}`} className="flex flex-wrap gap-2">
            <span className="rounded-full border border-border/60 px-2 py-0.5">{s.tool}</span>
            <span>{s.status}</span>
            <span>{s.items} results</span>
            <span>{Math.round(s.ms)}ms</span>
          </li>
        ))}
      </ul>
      <p className="mt-2">
        stop: {trace.stopReason} · tools: {trace.toolCalls} · replans: {trace.replans} · sources:{" "}
        {trace.sourceCount} · {Math.round(trace.totalMs)}ms
      </p>
    </details>
  );
}
